import { useId } from 'react'
import { useCourseGeometry } from '../hooks/useCourseGeometry'
import { usePageTitle } from '../hooks/usePageTitle'
import { useSharedRoundMedia } from '../hooks/useRoundMedia'
import type { SharedRoundMediaBook } from '../hooks/useRoundMedia'
import { useSharedRound } from '../hooks/useSharedRound'
import { formatPlayedOn, formatToPar } from '../lib/golfFormat'
import type { CourseGeometry, SharedRound, SharedRoundPlayer } from '../lib/golfRound'
import { PRODUCT_LONG_NAME } from '../lib/product'
import { roundMediaHoleCounts } from '../lib/roundMedia'
import { romanYear } from '../lib/roman'
import { seated, standingOf } from '../lib/standings'
import {
  AreaSectionHead,
  ClubLinkButton,
  ClubPage,
  Container,
  EmptyPlate,
  HoleTraceStage,
  RoundMediaSection,
  RoundScorecard,
  RoundSummaryCard,
  ShareHero,
} from '../components/golf'

/**
 * The public face of a posted round — `/r/:token`, readable by anyone holding
 * the link, noindexed by the shell.
 *
 * ## POSTCARD FIRST, and the detail scrolls beneath
 *
 * The maintainer's binding decision (decision record §4, 2026-08-22 —
 * `../../../../../docs/fireside/club-web-decisions.md`). Someone
 * opens this from a message: the first screen is a single self-contained card
 * that says the whole afternoon and survives being screenshotted, and the round
 * in full is underneath for the reader who stayed. `ShareHero` carries that
 * card — and the page's `h1` with it.
 *
 * ## ONE flag CTA, and it is at the FOOT
 *
 * The club spends one hot accent per page. This page's is the join rail: the
 * ask a stranger who just read somebody's round can actually answer. That is
 * the whole reason `chrome="share"` exists — `ClubHeader` would have spent the
 * budget on **Apply** before the reader saw a single score. The rail renders in
 * every state, including behind the dead-token plate, because it is the page's
 * frame rather than part of the round.
 *
 * ## `?hole=N` is a HOLE NUMBER, and it reframes the page
 *
 * A share sent from one hole in the Caddie app carries `?hole=7`. When it is
 * present the holes section moves to the TOP of the detail, wearing a brass
 * frame and a "shared to you" tag, and the stage opens on that hole. When it is
 * absent the holes sit in their natural place after the card and no "shared to
 * you" framing appears anywhere.
 *
 * A hole number this round never PLAYED (`?hole=3` on a back nine) is treated
 * as absent, and that is not belt-and-braces over `HoleTraceStage`'s own silent
 * degradation: the stage would quietly fall back to hole 10 while the frame
 * still said "Hole 3 · shared to you", which is the club captioning a drawing
 * with a hole nobody played. Refusing to frame is the honest degradation.
 *
 * A hole the round played but nobody TRACKED is absent for exactly the same
 * reason. The stage now opens on the first hole with shots on it and greys the
 * rest of the rail out, so a "Hole 7 · shared to you" tag over it would sit
 * above a stage that cannot open on 7 — the caption would simply be a lie.
 *
 * ## No session is read on this page. None.
 *
 * No `/me`, no auth context, no membership gate, and no share token reprinted
 * (the reader arrived holding it). `sharedRound.test.tsx` renders this screen
 * with no `AuthProvider` above it — which `useAuth` would throw on — so any
 * session read wired in here fails the suite rather than quietly shipping.
 */
export function SharedRoundScreen({ token, hole }: { token: string; hole: number | null }) {
  const { round, isLoading, dead } = useSharedRound(token)
  const mediaBook = useSharedRoundMedia(token, round !== null)
  // Public and ETag-revalidated by the browser — see `useCourseGeometry`. It
  // waits on the round because the round is what names the course.
  const { geometry } = useCourseGeometry(round?.course.key ?? null)

  usePageTitle(round ? `${round.course.name}, ${formatPlayedOn(round.played_on)}` : 'A round, shared')

  // Media may still belong to a played hole nobody traced. Keep that target
  // for the media section, while framing the trace only when the stage can
  // honestly open on it.
  const mediaHole =
    round !== null && hole !== null && round.hole_numbers.includes(hole) ? hole : null
  const focused =
    round !== null && mediaHole !== null && tracked(round, mediaHole) ? mediaHole : null
  const linkDead = dead || mediaBook.dead

  const view: SectionProps | null =
    round === null
      ? null
      : {
          round,
          geometry,
          mediaBook,
          standing: standingOf(round.players),
          hole: focused,
          mediaHole,
        }

  return (
    <ClubPage chrome="share">
      {isLoading && (
        <Container className="pb-16 pt-16 text-center">
          <p className="text-[14.5px] text-ink-soft">Opening the round…</p>
        </Container>
      )}

      {/* The club does not distinguish a link that was retired from one that
          never existed, so neither does this plate: a dead token, a round taken
          back for a correction and a string somebody typed all read the same,
          which is what keeps a token from being probeable. */}
      {!isLoading && linkDead && (
        <Container className="max-w-[640px] pb-16 pt-16 text-center">
          <EmptyPlate
            title="No such round in the book."
            description="A share link opens a round for as long as it stays posted in the club’s book. This one opens nothing — the round may have been taken back for a correction, or the link may never have been one."
          />
        </Container>
      )}

      {!isLoading && !linkDead && view && (
        <>
          <ShareHero
            courseName={view.round.course.name}
            playedOn={view.round.played_on}
            holesCount={view.round.holes_count}
            players={view.standing.map((player) => ({
              id: player.id,
              displayName: player.display_name,
              gross: player.total_strokes,
              toPar: player.summary?.to_par ?? null,
            }))}
          />

          <Container className="pb-4 pt-10">
            <div className="flex flex-col gap-14">
              {sectionsFor(focused).map((key) => {
                const Section = SECTION_VIEWS[key]
                return <Section key={key} {...view} />
              })}
            </div>
          </Container>
        </>
      )}

      <JoinRail />
    </ClubPage>
  )
}

// ── The layout ─────────────────────────────────────────────────────────────

type SectionKey = 'sharedHole' | 'media' | 'scoreboard' | 'scorecard' | 'holes' | 'summaries'

/**
 * THE PAGE, as an ordered array — the same idiom as the member round page, and
 * for the maintainer's same reason (decision record §1, layout is ordered
 * config — `../../../../../docs/fireside/club-web-decisions.md`). The ONE
 * thing `?hole=N` changes structurally is which of these two
 * orders runs: the shared hole leads and initializes media to that hole, or
 * the holes sit where they belong in the normal round scroll.
 */
function sectionsFor(hole: number | null): readonly SectionKey[] {
  return hole === null
    ? ['scoreboard', 'media', 'scorecard', 'holes', 'summaries']
    : ['sharedHole', 'media', 'scoreboard', 'scorecard', 'summaries']
}

interface SectionProps {
  round: SharedRound
  geometry: CourseGeometry | null
  mediaBook: SharedRoundMediaBook
  /** Lowest gross first — computed once and shared by the postcard and the band. */
  standing: readonly SharedRoundPlayer[]
  /** The `?hole=N` the link carried — null unless this round played it. */
  hole: number | null
  /** The played hole media should open on, even when nobody traced it. */
  mediaHole: number | null
}

const SECTION_VIEWS: Record<SectionKey, React.ComponentType<SectionProps>> = {
  sharedHole: SharedHoleSection,
  media: MediaSection,
  scoreboard: ScoreboardSection,
  scorecard: ScorecardSection,
  holes: HolesSection,
  summaries: SummariesSection,
}

// ── The sections ───────────────────────────────────────────────────────────

/**
 * The hole the sharer pointed at — framed, tagged, and first.
 *
 * ## The semantics are a NAMED REGION, deliberately not `aria-current`
 *
 * `aria-current` marks one item of a SET as the current one, and there is no
 * set here: this is a single framed area, and the statement "hole 7 of the
 * eighteen is the one on stage" already exists where it belongs — on the
 * stage's own rail, whose chips are radios and whose chosen chip carries
 * `aria-checked`. Putting `aria-current` on this wrapper would be a second,
 * weaker copy of that with nothing to be current among.
 *
 * What a reader actually needs is to be able to find and name this area, so it
 * is a `<section>` named by its own tag — "Hole 7 · shared to you" — which
 * gives it `role="region"` and a landmark to jump to. The other sections stay
 * unnamed generics on purpose: one landmark that means something beats five
 * that do not.
 *
 * `hole` is non-null here BY CONSTRUCTION: `sectionsFor` lists this section
 * only when the link named a hole this round actually played, and the screen
 * has already refused to frame one it did not. The prop is nullable because the
 * registry hands every section the same props, not because this one renders
 * without a hole — it never does, so there is no null branch to take.
 */
function SharedHoleSection({ round, geometry, mediaBook, hole }: SectionProps) {
  const tagId = useId()

  return (
    <section data-round-section="sharedHole" aria-labelledby={tagId}>
      <AreaSectionHead eyebrow="Shared to you" title="The hole they sent you." />

      <div className="relative rounded-club-lg border-[1.5px] border-brass bg-parchment px-4 pb-4 pt-6 sm:px-5">
        {/* The tag straddles the frame, as the card draws it. brass-DEEP, not
            brass: at 9px this is normal-size text under WCAG and plain brass
            on paper is 3.38:1. */}
        <span
          id={tagId}
          data-shared-hole-tag={hole}
          className="absolute -top-2.5 left-4 rounded-club-sm border border-brass bg-paper px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.2em] text-brass-deep"
        >
          Hole {hole} · shared to you
        </span>

        <HoleTraceStage
          holeNumbers={round.hole_numbers}
          courseHoles={round.course.holes}
          courseName={round.course.name}
          geometry={geometry}
          initialHole={hole}
          mediaCounts={roundMediaHoleCounts(mediaBook.media)}
          players={tracePlayers(round)}
        />
      </div>
    </section>
  )
}

function MediaSection({ mediaBook, mediaHole }: SectionProps) {
  return (
    <RoundMediaSection
      media={mediaBook.media}
      isLoading={mediaBook.isLoading}
      initialHole={mediaHole}
    />
  )
}

/**
 * The same scoreboard band the member round page draws — an ORDERED LIST rather
 * than the design card's `role="img"` band, because the order IS the
 * information and an `<ol>` states it to a screen reader for free.
 *
 * A LOCAL COPY of the MARKUP, and knowingly so: the member page's sections are
 * private to that screen (they close over a viewer id and the member `Round`
 * shape, which this page has neither of — there is no "You" here, because
 * nobody is signed in). Sharing them would mean exporting a section that takes
 * a shape this page cannot supply. The two are ~15 lines each and drift in
 * different directions; if a third round view ever appears, THAT is when a
 * shared band earns its props.
 *
 * The ORDER is a different matter and is not copied: both pages get it from
 * `lib/standings`, because two hand-rolled sorts is how one of them eventually
 * puts an unfinished card first.
 */
function ScoreboardSection({ standing }: SectionProps) {
  return (
    <section data-round-section="scoreboard">
      {/* ONE head over the band AND the table beneath it, as the design card
          draws them: they are the same statement at two resolutions, and the
          scorecard section deliberately carries no head of its own. */}
      <AreaSectionHead eyebrow="The card" title="Where it finished, stroke by stroke." />
      <ol className="flex flex-wrap overflow-hidden rounded-club-lg bg-pine text-paper" data-scoreboard="">
        {standing.map((player, index) => (
          <li
            key={player.id}
            data-scoreboard-seat={player.id}
            className="flex-[1_1_150px] border-r border-sage-soft/20 px-4 py-3 last:border-r-0"
          >
            <span className="block text-[9px] font-semibold uppercase tracking-[0.2em] text-gilt">
              {romanYear(index + 1)} · {player.display_name}
            </span>
            <span className="font-display text-[20px] tabular-nums">
              {player.total_strokes ?? '—'}
              {player.summary?.to_par !== null && player.summary?.to_par !== undefined && (
                <small className="ml-1.5 text-[11px] text-sage">
                  {formatToPar(player.summary.to_par)}
                </small>
              )}
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

/**
 * The card, in full.
 *
 * No `isYou` and no `declined` chip: nobody is signed in, and the public shape
 * withholds the consent timestamps on purpose (`SharedRound`). A page that
 * guessed at either would be inventing a fact about a person from a payload
 * that deliberately does not carry it.
 */
function ScorecardSection({ round, mediaBook }: SectionProps) {
  return (
    <section data-round-section="scorecard">
      <RoundScorecard
        holeNumbers={round.hole_numbers}
        courseHoles={round.course.holes}
        courseName={round.course.name}
        mediaCounts={roundMediaHoleCounts(mediaBook.media)}
        players={seated(round.players).map((player) => ({
          id: player.id,
          position: player.position,
          displayName: player.display_name,
          guest: player.guest,
          scores: player.scores,
        }))}
      />
    </section>
  )
}

/** The holes in their natural place — the link named no hole to lead with. */
function HolesSection({ round, geometry, mediaBook }: SectionProps) {
  return (
    <section data-round-section="holes">
      <AreaSectionHead eyebrow="The holes" title="As they were played." />
      <HoleTraceStage
        holeNumbers={round.hole_numbers}
        courseHoles={round.course.holes}
        courseName={round.course.name}
        geometry={geometry}
        mediaCounts={roundMediaHoleCounts(mediaBook.media)}
        players={tracePlayers(round)}
      />
    </section>
  )
}

/**
 * How their days went — condensed.
 *
 * The public shape OMITS `index_after` and `index_delta`, so `HandicapMovement`
 * inside each card renders nothing at all and the cards close on the medals.
 * That needs no branch here and must not grow one: the absence is the club
 * declining to put a live-derived number on a public archive, and a screen that
 * special-cased it would be a second place for that decision to be forgotten.
 */
function SummariesSection({ round }: SectionProps) {
  return (
    <section data-round-section="summaries">
      <AreaSectionHead eyebrow="The day" title="How their days went." />
      <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-3">
        {seated(round.players).map((player) => (
          <RoundSummaryCard
            key={player.id}
            player={{
              id: player.id,
              position: player.position,
              displayName: player.display_name,
              guest: player.guest,
              indexAtPlay: player.index_at_play,
              scoreDifferential: player.score_differential,
            }}
            summary={player.summary}
          />
        ))}
      </div>
    </section>
  )
}

/**
 * The page's ONE flag CTA — and the only thing on this page that asks the
 * reader for anything.
 *
 * Renders in every state, the dead-token plate included: it belongs to the
 * page's frame rather than to the round, and someone who followed a link that
 * had expired is exactly as good a candidate for a seat as someone who read the
 * whole card.
 */
function JoinRail() {
  return (
    <section data-join-rail="" className="mt-14 border-t border-brass/35 bg-paper-deep">
      <Container className="flex flex-wrap items-center gap-x-6 gap-y-4 py-7">
        <div className="flex-[1_1_320px]">
          <h2 className="font-display text-[22px] font-medium text-pine">
            The club keeps every round like this.
          </h2>
          <p className="mt-1 max-w-[56ch] text-[13px] text-ink-soft">
            {PRODUCT_LONG_NAME} — a private virtual club, chartered MMXXVI. One hundred founding
            seats; a book that remembers every card, every trace, every index.
          </p>
        </div>
        <ClubLinkButton to="/membership">Take up a founding seat</ClubLinkButton>
      </Container>
    </section>
  )
}

// ── Derivations ────────────────────────────────────────────────────────────

/** Did ANY seat track a shot on this hole — the stage's own opening rule. */
function tracked(round: SharedRound, hole: number): boolean {
  return round.players.some((player) => player.shots.some((shot) => shot.hole_number === hole))
}

/** Every seat, in seat order, as the stage draws them. */
function tracePlayers(round: SharedRound) {
  return seated(round.players).map((player) => ({
    id: player.id,
    position: player.position,
    displayName: player.display_name,
    shots: player.shots,
    scores: player.scores,
  }))
}
